import { Tables } from '@/supabase/types'
import { LLM, LLMID, ModelProvider, OpenRouterLLM } from '@/types'
import { toast } from 'sonner'
import i18next from 'i18next'

import { PLAN_FREE, PLAN_PRO, PLAN_ULTRA } from '../stripe/config'
import { CATEGORIES } from './categories'
import { LLM_LIST_MAP } from './llm/llm-list'

const KNOWN_MODEL_NAMES: {
  [key: string]: Partial<LLM>
} = {
  'databricks/dbrx-instruct': {
    provider: 'databricks',
    modelName: 'DBRX Instruct'
  },
  'cohere/command-r-plus': {
    provider: 'cohere',
    modelName: 'Command R Plus'
  },
  'mistralai/mixtral-8x22b-instruct': {
    provider: 'mistral',
    modelName: 'Mixtral 8x22B'
  },
  'meta-llama/llama-3-70b-instruct': {
    provider: 'meta',
    modelName: 'Meta Llama 3 70B'
  },
  'microsoft/wizardlm-2-8x22b': {
    provider: 'microsoft',
    modelName: 'WizardLM 2 8x22B'
  },
  'deepseek/deepseek-coder': {
    provider: 'deepseek',
    modelName: 'DeepSeek Coder V2',
    categories: [CATEGORIES.PROGRAMMING]
  },
  'gryphe/mythomax-l2-13b': {
    provider: 'gryphe' as any,
    modelName: 'Mythomax 13B',
    categories: [CATEGORIES.ROLEPLAY]
  }
}

type OllamaApiPath = `/api/${string}`

const INVALID_OLLAMA_BASE_URLS = new Set(['undefined', 'null'])

const getNormalizedOllamaBaseUrl = () => {
  const rawBaseUrl = process.env.NEXT_PUBLIC_OLLAMA_URL?.trim()

  if (!rawBaseUrl) {
    return null
  }

  const normalizedBaseUrl = rawBaseUrl.replace(/\/+$/, '')

  if (INVALID_OLLAMA_BASE_URLS.has(normalizedBaseUrl.toLowerCase())) {
    return null
  }

  const candidateBaseUrl =
    /^[a-z][a-z\d+\-.]*:\/\//i.test(normalizedBaseUrl) || normalizedBaseUrl.startsWith('/')
      ? normalizedBaseUrl
      : `http://${normalizedBaseUrl}`

  try {
    const appOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    return new URL(`${candidateBaseUrl}/`, appOrigin).toString()
  } catch {
    return null
  }
}

export const resolveOllamaApiUrl = (path: OllamaApiPath) => {
  const baseUrl = getNormalizedOllamaBaseUrl()

  if (!baseUrl) {
    return null
  }

  return new URL(path.replace(/^\//, ''), baseUrl).toString()
}

export const fetchHostedModels = async (profile: Tables<'profiles'> | null | undefined) => {
  try {
    const providers = [
      'marshmallow',
      'anthropic',
      'google',
      'mistral',
      'groq',
      'perplexity',
      'openai',
      'bedrock'
    ]

    if (profile?.use_azure_openai) {
      providers.push('azure')
    } else {
      providers.push('openai')
    }

    const response = await fetch('/api/keys')

    if (!response.ok) {
      throw new Error(i18next.t('models.errors.server_not_responding'))
    }

    const data = await response.json()

    let modelsToAdd: LLM[] = []

    for (const provider of providers) {
      const models = LLM_LIST_MAP[provider]

      if (!Array.isArray(models)) {
        continue
      }

      if (profile) {
        let providerKey: keyof typeof profile

        if (provider === 'google') {
          providerKey = 'google_gemini_api_key'
        } else if (provider === 'azure') {
          providerKey = 'azure_openai_api_key'
        } else {
          providerKey = `${provider}_api_key` as keyof typeof profile
        }

        if (profile?.[providerKey]) {
          modelsToAdd.push(...models)
          continue
        }
      }

      if (data.isUsingEnvKeyMap[provider]) {
        modelsToAdd.push(...models)
      }
    }

    return {
      envKeyMap: data.isUsingEnvKeyMap,
      hostedModels: modelsToAdd
    }
  } catch (error) {
    console.warn(i18next.t('models.errors.hosted_models_error', { error }))
  }
}

export const fetchOllamaModels = async () => {
  try {
    const ollamaUrl = resolveOllamaApiUrl('/api/tags')

    if (!ollamaUrl) {
      return []
    }

    console.log('Attempting to fetch Ollama models from:', ollamaUrl)

    const response = await fetch(ollamaUrl)

    if (!response.ok) {
      throw new Error(i18next.t('models.errors.ollama_not_responding'))
    }

    const data = await response.json()

    const localModels: LLM[] = data.models.map((model: any) => ({
      modelId: model.name as LLMID,
      modelName: model.name,
      provider: 'ollama',
      hostedId: model.name,
      platformLink: 'https://ollama.ai/library',
      imageInput: false
    }))

    return localModels
  } catch (error) {
    console.warn(i18next.t('models.errors.hosted_models_error', { error }))
  }
}

export const fetchOpenRouterModels = async () => {
  try {
    const response = await fetch('/api/openrouter-models')

    if (!response.ok) {
      throw new Error(i18next.t('models.errors.server_not_responding'))
    }

    const { data } = await response.json()
    return data as OpenRouterLLM[]
  } catch (error) {
    console.error(i18next.t('models.errors.openrouter_error', { error }))
    toast.error(i18next.t('models.errors.openrouter_error', { error }))
    return [] // Return an empty array in case of error
  }
}
